import {
  provideTaiga,
  tuiButtonOptionsProvider,
  tuiIconResolverProvider,
  tuiTextfieldOptionsProvider,
} from '@taiga-ui/core';
import { ApplicationConfig, provideBrowserGlobalErrorListeners, signal } from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';

import { provideNgxStripe } from 'ngx-stripe';

import { routes } from './app.routes';
import { authInterceptor } from '@core/interceptors/auth-interceptor';
import { AppTitleStrategy } from '@core/router/app-title.strategy';
import { AppViewportScroller } from '@core/router/app-viewport-scroller';
import { provideAuth } from '@core/providers/auth.provider';
import { provideAnalytics } from '@core/providers/analytics.provider';
import { provideIntercom } from '@core/providers/intercom.provider';
import { provideProductFruits } from '@core/providers/product-fruits.provider';
import { provideClassicApp } from '@core/providers/classic-app.provider';
import { environment } from '@environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    // The app scrolls an inner container, not the window — see AppViewportScroller.
    { provide: ViewportScroller, useExisting: AppViewportScroller },
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    // First, so a user who chose the classic app is redirected before anything else starts.
    provideClassicApp(),
    // Registered before provideAuth() so mixpanel.init() has completed by the
    // time the auth initializer resolves a session and calls identify().
    provideAnalytics(),
    provideAuth(),
    provideIntercom(),
    provideProductFruits(),
    provideNgxStripe(environment.stripeKey),
    provideTaiga(),
    tuiIconResolverProvider((name: string) => {
      if (name.startsWith('@img.')) {
        return `custom-icons/${name.replace('@img.', '')}.svg`;
      }

      const taigaName = name.replace(/^@tui\./, '');
      return `assets/taiga-ui/icons/${taigaName.replaceAll('.', '/')}.svg`;
    }),
    tuiTextfieldOptionsProvider({ size: signal('m'), appearance: signal('outline') }),
    tuiButtonOptionsProvider({ size: 's' }),
  ],
};
